import api from "./api";
import manifest from "../config/nav_manifest.json";

// All grantable Tier-2 page_ids (route paths) from the shared manifest.
const LEAF_PAGE_IDS = manifest.sections.flatMap((s) =>
  (s.children || []).map((c) => c.page_id)
);

/**
 * Resolve a browser pathname to its access-matrix page_id via longest-prefix
 * match (so /deals/orphan-quotes maps to /deals). Returns null for paths not
 * governed by the matrix (e.g. /home, /profile, detail routes) — those are
 * never access-gated on the client.
 */
export function resolvePageId(pathname) {
  let best = null;
  for (const id of LEAF_PAGE_IDS) {
    if (pathname === id || pathname.startsWith(id + "/")) {
      if (!best || id.length > best.length) best = id;
    }
  }
  return best;
}

export const ACCESS_ROLE_LABELS = {
  view_only: "View Only",
  operator: "Operator",
  finance: "Finance",
  manager: "Management",
  admin: "Admin",
  supplier: "Supplier",
};

export const ACCESS_ROLE_ORDER = ["admin", "manager", "operator", "finance", "supplier", "view_only"];
export const PRESET_ROLE_ORDER = ["admin", "manager", "operator", "finance", "supplier"];
export const ROLE_DEFAULT_LEVEL = { admin: "manage", manager: "manage", operator: "edit", finance: "edit", supplier: "view", view_only: "view" };

// Per-person diff vs the role preset → { access_overrides, access_levels }.
export function diffFromPreset(role, levels, presetLevels) {
  const P = role === "view_only" ? {} : presetLevels || {};
  const dflt = ROLE_DEFAULT_LEVEL[role] || "edit";
  const access_overrides = [];
  const access_levels = {};
  Object.keys(levels).forEach((id) => {
    if (!(id in P)) access_overrides.push({ page_id: id, granted: true });
    if (levels[id] !== (P[id] || dflt)) access_levels[id] = levels[id];
  });
  Object.keys(P).forEach((id) => { if (!(id in levels)) access_overrides.push({ page_id: id, granted: false }); });
  return { access_overrides, access_levels };
}

export async function fetchNavTree() {
  const { data } = await api.get("/access/nav-tree");
  return data.pages;
}

export async function fetchPresets() {
  const { data } = await api.get("/access/presets");
  return data.presets;
}

export async function savePreset(role, levels, capabilities) {
  const { data } = await api.put(`/access/presets/${role}`, {
    pages: Object.keys(levels).map((page_id) => ({ page_id, granted: true })),
    levels,
    ...(capabilities ? { capabilities } : {}),
  });
  return data;
}

export async function fetchAccessUsers() {
  const { data } = await api.get("/access/users");
  return data.users;
}

export async function fetchAccessUser(id) {
  const { data } = await api.get(`/access/users/${id}`);
  return data;
}

export async function patchAccessUser(id, payload) {
  const { data } = await api.patch(`/access/users/${id}`, payload);
  return data;
}

export async function requestAccess(pageId, note) {
  const { data } = await api.post(`/access/requests`, { page_id: pageId, note });
  return data;
}

export async function fetchAccessRequests(status = "pending") {
  const { data } = await api.get(`/access/requests`, { params: { status } });
  return data;
}

export async function approveAccessRequest(id) {
  const { data } = await api.post(`/access/requests/${id}/approve`);
  return data;
}

export async function dismissAccessRequest(id) {
  const { data } = await api.post(`/access/requests/${id}/dismiss`);
  return data;
}

// Group flat nav-tree into [{section, children[]}] for the matrix UI.
export function groupTree(pages) {
  const sections = pages.filter((p) => p.tier === 1);
  return sections.map((s) => ({
    ...s,
    children: pages.filter((p) => p.tier === 2 && p.parent_id === s.page_id),
  }));
}
